import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector } from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/query';

import { baseApi } from '../services';

import uiSlice from './slices/ui.slice';
import globalSlice from './slices/global.slice';

import routeResetMiddleware from './middleware/routeResetMiddleware';

/**
 * Redux store configuration
 * Combines all slices and middleware for state management
 * Includes route reset middleware to clear page-specific state on navigation
 */
export const store = configureStore({
  reducer: {
    [baseApi.reducerPath]: baseApi.reducer,
    global: globalSlice,
    ui: uiSlice
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['upload/setSelectedFile'],
        ignoredPaths: ['upload.fileObject']
      }
    })
      .concat(routeResetMiddleware)
      .concat(baseApi.middleware),
  devTools:
    process.env.NODE_ENV !== 'production'
      ? {
          maxAge: 25
        }
      : false
});

setupListeners(store.dispatch);
/**
 * Root state type derived from store
 */
export type RootState = ReturnType<typeof store.getState>;

/**
 * App dispatch type
 */
export type AppDispatch = typeof store.dispatch;

/**
 * Typed useDispatch hook for use throughout the app
 * @returns {AppDispatch} Typed dispatch function
 */
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();

/**
 * Typed useSelector hook for use throughout the app
 * @returns {RootState} Typed selector function
 */
export const useAppSelector = useSelector.withTypes<RootState>();
